export type ControllerLayout =
  | "8bitdo-ultimate-2"
  | "8bitdo-pro-3"
  | "8bitdo"
  | "xbox"
  | "playstation"
  | "generic";

export type ButtonKey =
  | "south"
  | "east"
  | "west"
  | "north"
  | "leftBumper"
  | "rightBumper"
  | "leftTrigger"
  | "rightTrigger"
  | "select"
  | "start"
  | "leftStick"
  | "rightStick"
  | "dpadUp"
  | "dpadDown"
  | "dpadLeft"
  | "dpadRight"
  | "home"
  | "leftRear"
  | "rightRear"
  | "leftPaddle"
  | "rightPaddle";

export type AxisKey = "leftStickX" | "leftStickY" | "rightStickX" | "rightStickY";

export interface ControlDefinition<T extends string> {
  key: T;
  label: string;
  short: string;
  group: "face" | "shoulder" | "system" | "dpad" | "extra" | "stick";
}

export const BUTTON_CONTROLS: ControlDefinition<ButtonKey>[] = [
  { key: "south", label: "Face South", short: "A", group: "face" },
  { key: "east", label: "Face East", short: "B", group: "face" },
  { key: "west", label: "Face West", short: "X", group: "face" },
  { key: "north", label: "Face North", short: "Y", group: "face" },
  { key: "leftBumper", label: "Left Bumper", short: "LB", group: "shoulder" },
  { key: "rightBumper", label: "Right Bumper", short: "RB", group: "shoulder" },
  { key: "leftTrigger", label: "Left Trigger", short: "LT", group: "shoulder" },
  { key: "rightTrigger", label: "Right Trigger", short: "RT", group: "shoulder" },
  { key: "select", label: "Select / View", short: "−", group: "system" },
  { key: "start", label: "Start / Menu", short: "+", group: "system" },
  { key: "leftStick", label: "Left Stick Press", short: "L3", group: "stick" },
  { key: "rightStick", label: "Right Stick Press", short: "R3", group: "stick" },
  { key: "dpadUp", label: "D-pad Up", short: "↑", group: "dpad" },
  { key: "dpadDown", label: "D-pad Down", short: "↓", group: "dpad" },
  { key: "dpadLeft", label: "D-pad Left", short: "←", group: "dpad" },
  { key: "dpadRight", label: "D-pad Right", short: "→", group: "dpad" },
  { key: "home", label: "Home / Guide", short: "◎", group: "system" },
  { key: "leftRear", label: "Left Rear", short: "L4", group: "extra" },
  { key: "rightRear", label: "Right Rear", short: "R4", group: "extra" },
  { key: "leftPaddle", label: "Left Paddle", short: "PL", group: "extra" },
  { key: "rightPaddle", label: "Right Paddle", short: "PR", group: "extra" },
];

export const AXIS_CONTROLS: ControlDefinition<AxisKey>[] = [
  { key: "leftStickX", label: "Left Stick X", short: "LX", group: "stick" },
  { key: "leftStickY", label: "Left Stick Y", short: "LY", group: "stick" },
  { key: "rightStickX", label: "Right Stick X", short: "RX", group: "stick" },
  { key: "rightStickY", label: "Right Stick Y", short: "RY", group: "stick" },
];

export type ButtonMap = Partial<Record<ButtonKey, number>>;
export type AxisMap = Partial<Record<AxisKey, { index: number; invert?: boolean }>>;

export interface MappingProfile {
  schema: "simtile.gamepad-mapping/v1";
  profileId: string;
  name: string;
  match: {
    id: string;
    mapping?: string;
  };
  layout: ControllerLayout;
  buttons: ButtonMap;
  axes: AxisMap;
  deadzone: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export const STANDARD_BUTTONS: ButtonMap = {
  south: 0,
  east: 1,
  west: 2,
  north: 3,
  leftBumper: 4,
  rightBumper: 5,
  leftTrigger: 6,
  rightTrigger: 7,
  select: 8,
  start: 9,
  leftStick: 10,
  rightStick: 11,
  dpadUp: 12,
  dpadDown: 13,
  dpadLeft: 14,
  dpadRight: 15,
  home: 16,
};

export const STANDARD_AXES: AxisMap = {
  leftStickX: { index: 0 },
  leftStickY: { index: 1 },
  rightStickX: { index: 2 },
  rightStickY: { index: 3 },
};

export const PROFILE_STORAGE_KEY = "simtile.gamepad-lab.profiles.v1";

export function detectLayout(id: string): ControllerLayout {
  const value = id.toLowerCase();
  if (value.includes("8bitdo") && /ultimate\s*2/.test(value)) return "8bitdo-ultimate-2";
  if (value.includes("8bitdo") && /pro\s*3/.test(value)) return "8bitdo-pro-3";
  if (value.includes("8bitdo")) return "8bitdo";
  if (/dualsense|dualshock|playstation|wireless controller|sony/.test(value)) return "playstation";
  if (/xbox|xinput|microsoft/.test(value)) return "xbox";
  return "generic";
}

export function slugifyId(id: string): string {
  const slug = id
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 56);
  return slug || "unknown-gamepad";
}

export function createProfile(
  gamepad: { id: string; mapping: string },
  useStandard = gamepad.mapping === "standard",
): MappingProfile {
  const now = new Date().toISOString();
  return {
    schema: "simtile.gamepad-mapping/v1",
    profileId: slugifyId(gamepad.id),
    name: gamepad.id || "Unknown gamepad",
    match: { id: gamepad.id, mapping: gamepad.mapping },
    layout: detectLayout(gamepad.id),
    buttons: useStandard ? { ...STANDARD_BUTTONS } : {},
    axes: useStandard ? structuredClone(STANDARD_AXES) : {},
    deadzone: 0.12,
    createdAt: now,
    updatedAt: now,
  };
}

export function loadProfiles(): MappingProfile[] {
  try {
    const stored = localStorage.getItem(PROFILE_STORAGE_KEY);
    if (!stored) return [];
    const data: unknown = JSON.parse(stored);
    if (!Array.isArray(data)) return [];
    return data.filter(
      (item): item is MappingProfile =>
        typeof item === "object" &&
        item !== null &&
        (item as MappingProfile).schema === "simtile.gamepad-mapping/v1" &&
        typeof (item as MappingProfile).match?.id === "string",
    );
  } catch {
    return [];
  }
}

export function saveProfiles(profiles: MappingProfile[]): void {
  localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profiles));
}

export function resolveProfile(profiles: MappingProfile[], gamepadId: string): MappingProfile | undefined {
  return profiles.find((profile) => profile.match.id === gamepadId);
}

export function mappedButtonValue(
  buttons: Array<{ value: number }>,
  profile: MappingProfile,
  key: ButtonKey,
): number {
  const index = profile.buttons[key];
  return index === undefined ? 0 : (buttons[index]?.value ?? 0);
}

export function mappedAxisValue(
  axes: number[],
  profile: MappingProfile,
  key: AxisKey,
): number {
  const binding = profile.axes[key];
  if (!binding) return 0;
  const raw = axes[binding.index] ?? 0;
  const value = binding.invert ? -raw : raw;
  return Math.abs(value) < profile.deadzone ? 0 : value;
}
